import React, { useState, KeyboardEvent, ClipboardEvent } from 'react';
import { isEmpty } from 'lodash-es';
import styled, { css } from 'styled-components';
import { TextField as RapydTextField } from '@rapyd/react-ui-library';
import { TextFieldProps as RapydTextFieldProps } from '@rapyd/react-ui-library/dist/components/Form/TextField';
import { ThemeBreakpoints } from '@rapyd/react-ui-library/dist/theme/breakpoints';
import { isSafari, isMobile } from 'react-device-detect';
import { isValidValue } from 'components/layouts/sharedStyles';
import { isAlphaNumericOrSpace } from 'utilities';
import { FULLSTORY_MASK_CLASSNAME } from 'consts';
import { getClearCustomStyles } from 'components/core/styles';
import './InputStyle.css';

export interface ITextFieldProps extends RapydTextFieldProps {
  showDescriptionOnFocus?: boolean;
  ignoreSpecialCharacters?: boolean;
  hideDeleteButton?: boolean;
}

const browserToStyleMapping = {
  mobile: 'disabledFieldMobile',
  safariDesktop: 'disabledFieldSafariDesktop',
  defaultDesktop: 'disabledFieldDefaultDesktop'
};

const Input = ({
  className,
  name,
  fieldBoxProps,
  showDescriptionOnFocus = true,
  disabled,
  value,
  ignoreSpecialCharacters,
  hideDeleteButton,
  ...rest
}: ITextFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleBlur = (): void => {
    setIsFocused(false);
  };

  const handleFocus = (): void => {
    setIsFocused(true);
  };

  const preventSpecialCharacters = ({
    e,
    text
  }: {
    e: KeyboardEvent<HTMLInputElement> | ClipboardEvent<HTMLInputElement>;
    text: string;
  }) => {
    if (!ignoreSpecialCharacters) return;
    if (!isAlphaNumericOrSpace(text)) e.preventDefault();
  };

  let description;
  if (showDescriptionOnFocus) {
    description = isFocused ? fieldBoxProps?.description : '';
  } else {
    description = fieldBoxProps?.description;
  }

  let secondaryClassName;
  if (disabled) {
    if (isMobile) {
      secondaryClassName = browserToStyleMapping.mobile;
    } else {
      secondaryClassName = isSafari
        ? browserToStyleMapping.safariDesktop
        : browserToStyleMapping.defaultDesktop;
    }
  } else if (!isValidValue(value) && !isFocused) {
    secondaryClassName = 'hiddenLabel';
  }

  return (
    <StyledInput
      className={`${className} ${secondaryClassName} ${FULLSTORY_MASK_CLASSNAME}`}
      name={name || 'text-field'}
      value={value}
      disabled={disabled}
      $hideTooltip={!isEmpty(value)}
      $isActive={isFocused}
      $hideDeleteButton={!!hideDeleteButton}
      onKeyPress={(e: KeyboardEvent<HTMLInputElement>) => {
        preventSpecialCharacters({ e, text: e.key });
      }}
      onPaste={(e: ClipboardEvent<HTMLInputElement>) => {
        preventSpecialCharacters({ e, text: e.clipboardData.getData('Text') });
      }}
      fieldBoxProps={{
        ...fieldBoxProps,
        staticSize: ThemeBreakpoints.LARGE,
        description,
        onBlur: handleBlur,
        onFocus: handleFocus,
        isClearable: !disabled
      }}
      {...rest}
    />
  );
};

export default Input;

const getCustomStyles =
  () =>
  (
    inputProps: RapydTextFieldProps & { theme: Record<string, any> } & {
      $isActive: boolean;
    }
  ) => {
    const { name, theme, $isActive, disabled, fieldBoxProps } = inputProps || {};
    const isError = !isEmpty(fieldBoxProps?.error);

    const baseObject = theme?.hcp?.input;
    if (isEmpty(baseObject)) return;

    return css`
      fieldset {
        border-color: ${baseObject?.base?.borderColor};
        border-radius: ${baseObject?.base?.borderRadius};
        background-color: ${baseObject?.base?.backgroundColor};
      }

      input {
        font-size: ${baseObject?.base?.fontSize};
        font-weight: ${baseObject?.base?.fontWeight};
        font-family: ${baseObject?.base?.fontFamily};
        color: ${baseObject?.base?.color};

        &:focus {
          outline: ${baseObject?.focus?.outline};
        }
      }

      input[data-testid='${name}-input'] {
        z-index: 1;
      }

      ::placeholder {
        font-size: ${baseObject?.placeholder?.fontSize};
        font-weight: ${baseObject?.placeholder?.fontWeight};
        font-family: ${baseObject?.placeholder?.fontFamily};
        color: ${baseObject?.placeholder?.color};
      }

      ::selection {
        color: ${baseObject?.selection?.color};
        background-color: ${baseObject?.selection?.backgroundColor};
      }

      ${$isActive &&
      `
      fieldSet {
        border-color: ${baseObject?.active?.borderColor};
        background-color: ${baseObject?.active?.backgroundColor};
      }

      input {
        color: ${baseObject?.active?.color};
      }

      label[data-testid='${name}-label'] {
        color: ${baseObject?.active?.labelColor};
      }
    `};

      ${isError &&
      `
      fieldSet {
        border-color: ${baseObject?.error?.borderColor};
        background-color: unset;
      }

      input {
        color: ${baseObject?.error?.color};
      }

      label[data-testid='${name}-label'] {
        color: ${baseObject?.error?.labelColor};
      }

      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0 100rem ${theme.colors.surfaceFamilies.error} inset !important;
      }
    `};

      ${disabled &&
      `
      fieldSet {
        background-color: ${baseObject?.disabled?.backgroundColor};
      }

      input {
        color: ${baseObject?.disabled?.color};
      }
    `};
    `;
  };

const StyledInput = styled(RapydTextField)<{
  $hideTooltip?: boolean;
  $isActive: boolean;
  $hideDeleteButton: boolean;
}>`
  [class$='-addons-wrapper'] {
    z-index: 1;
  }

  .cardNumber-addons-wrapper > span {
    position: relative;
  }

  input {
    font-size: ${({ theme }) => theme.view.isMobile && theme.fonts.size.medium};
  }

  ${({ $isActive, value }) =>
    ($isActive || !!value) &&
    css`
      label,
      legend span {
        font-size: ${({ theme }) => theme.fonts.size.extraSmall};
      }
    `}

  ${({ $hideTooltip }) =>
    $hideTooltip &&
    css`
      [data-testid='tooltip'] {
        display: none;
      }
    `}

    ${({ $hideDeleteButton }) =>
    $hideDeleteButton &&
    css`
      [data-testid*='delete'] {
        display: none;
      }
    `}

  ${getCustomStyles()};
  ${getClearCustomStyles()};
`;
